import MemoIconREASolo from "./icons/IconREASolo.js"
import MemoIconREAPartner from "./icons/IconREAPartner.js"
import MemoIconREATeam from "./icons/IconREATeam.js"
import MemoIconREAFreemium from "./icons/IconREAFreemium.js"
import MemoIconCheckmarkCircle from "./icons/IconCheckmarkCircle.js"
import MemoIconHomebotApp from "./icons/IconHomebotApp.js"
import MemoIconLOStarter from "./icons/IconLOStarter.js"
import MemoIconLOPro from "./icons/IconLOPro.js"
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useFormState, useFormDispatch, updateField, setErrors, clearErrors, setSubmitting } from './FormStateMachine';
import { validatePaymentForm } from './validation';
import isEmail from 'validator/lib/isEmail';
import { getPlanDetails, getPlanCodesForCompany } from './planDetails';
import './SignUp.css';
import Select from 'react-select';
import {
  trackFieldInteraction,
  trackFormProgression,
  trackError,
  trackPromoCodeEvent,
  trackConversion,
  trackPageLoad,
  trackPlanSelection,
  trackPaymentMethodSelection,
  trackFormSubmissionAttempt,
  trackFormSubmissionFailure,
  trackSignupFailure,
  trackTermsAcceptance,
  trackPromoCodeSectionOpened
} from './utils/gtmTracking';

const SignUp = () => {
  const navigate = useNavigate();
  const {
    plan,
    sponsorId,
    capacity,
    paymentMethod,
    cardNumber,
    expiration,
    securityCode,
    cardFirstName,
    cardLastName,
    billingAddress,
    billingState,
    billingZip,
    billingCity,
    billingCountry,
    promoCode,
    accountNumber,
    accountNumberConfirmation,
    routingNumber,
    accountType,
    nameOnAccount,
    firstName,
    lastName,
    email,
    nmlsId,
    stateLicense,
    termsAccepted,
    errors,
    isSubmitting,
    showPromoCode,
    subscriptionLength,
    referral,
    companyId,
    selectedOffice
  } = useFormState();
  const dispatch = useFormDispatch();
  const formRef = useRef();
  const attachedElementsRef = useRef({
    cardNumber: false,
    cardMonth: false,
    cardYear: false,
    cardCvv: false,
    accountNumber: false,
    routingNumber: false,
    accountType: false,
    accountHolderName: false
  });
  const elementsRef = useRef(null);
  const observerRef = useRef(null);
  const [freemiumAvailable, setFreemiumAvailable] = useState(false);
  const [promoCodeValidation, setPromoCodeValidation] = useState({
    isValidating: false,
    isValid: null,
    discount: null,
    error: null
  });
  const [appliedPromoCodes, setAppliedPromoCodes] = useState([]);
  const [currentPromoInput, setCurrentPromoInput] = useState('');
  const [isAutoApplyingPromo, setIsAutoApplyingPromo] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [nmlsIdTouched, setNmlsIdTouched] = useState(false);
  const [stateLicenseTouched, setStateLicenseTouched] = useState(false);
  const [showTestimonialAnimation, setShowTestimonialAnimation] = useState(false);
  const [corporateProfile, setCorporateProfile] = useState(null);
  const [officeProfiles, setOfficeProfiles] = useState([]);
  const [companyPlans, setCompanyPlans] = useState([]);
  const [isLoadingCompanyData, setIsLoadingCompanyData] = useState(false);
  const [companyDataError, setCompanyDataError] = useState(null);

  // US state options for react-select
  const stateOptions = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AS", label: "American Samoa" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "DC", label: "District Of Columbia" },
    { value: "FM", label: "Federated States Of Micronesia" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "GU", label: "Guam" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MH", label: "Marshall Islands" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "MP", label: "Northern Mariana Islands" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PW", label: "Palau" },
    { value: "PA", label: "Pennsylvania" },
    { value: "PR", label: "Puerto Rico" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VI", label: "Virgin Islands" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" }
  ];

  // Add this near the other options arrays
  const accountTypeOptions = [
    { value: 'checking', label: 'Checking' },
    { value: 'savings', label: 'Savings' }
  ];

  // Helper function to get the full plan code
  const getFullPlanCode = (plan, subscriptionLength) => {
    const realEstatePlans = ['freemium-starter', 'hb-agent-solo', 'hb-agent-partner', 'hb-agent-team'];
    const insurancePlans = ['hb-insurance-agent-pro'];
    const titlePlans = ['hb-title-agent-pro'];
    
    // Check if this is a company-specific plan (from API)
    const isCompanyPlan = companyPlans.some(p => {
      const code = p.attributes?.code || p.attributes?.['plan-code'];
      return code === plan;
    });
    
    const monthlyOnlyPlans = [...realEstatePlans, ...insurancePlans, ...titlePlans];
    
    // Company plans are always monthly-only, or check if in the static monthly-only list
    return (isCompanyPlan || monthlyOnlyPlans.includes(plan)) ? plan : plan + '-' + subscriptionLength;
  };

  useEffect(() => {
    checkForFreemiumAvailability();
  }, [sponsorId]);

  // Fetch company data when companyId is available
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!companyId) {
        return;
      }

      setIsLoadingCompanyData(true);
      setCompanyDataError(null);

      try {
        // Fetch corporate profile with office-profiles
        const response = await fetch(
          `${process.env.REACT_APP_MIKASA_URL}/corporate-profiles/${companyId}?include=office-profiles`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              'Accept': 'application/vnd.api+json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch company data: ${response.status}`);
        }

        const responseData = await response.json();
        
        // Set corporate profile data
        console.log('responseData', responseData);
        if (responseData.data) {
          setCorporateProfile(responseData.data);
          
          // Extract plan codes from the corporate profile mapping
          const planCodes = getPlanCodesForCompany(companyId);

          // Fetch plans separately for each plan code
          if (planCodes && planCodes.length > 0) {
            // Fetch all plans in parallel
            const planPromises = planCodes.map(planCode =>
              fetch(`${process.env.REACT_APP_MIKASA_URL}/plans/signup/${planCode}`, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/vnd.api+json',
                  'Accept': 'application/vnd.api+json'
                }
              }).then(res => res.ok ? res.json() : null)
            );
            
            const planResponses = await Promise.all(planPromises);
            // Flatten and filter out null responses, then extract data arrays
            const allPlans = planResponses
              .filter(response => response && response.data)
              .flatMap(response => Array.isArray(response.data) ? response.data : [response.data]);
            
            setCompanyPlans(allPlans);
          }
        }

        // Set office profiles data
        if (responseData.included) {
          const offices = responseData.included.filter(
            item => item.type === 'office-profiles'
          );
          setOfficeProfiles(offices);
        }

      } catch (error) {
        console.error('Error fetching company data:', error);
        setCompanyDataError(error.message);
      } finally {
        setIsLoadingCompanyData(false);
      }
    };

    fetchCompanyData();
  }, [companyId]);

  // Set plan to company's plan when companyPlans are loaded
  useEffect(() => {
    if (companyPlans.length > 0) {
      const companyPlan = companyPlans[0];
      const planCode = companyPlan.attributes?.code || companyPlan.attributes?.['plan-code'];
      if (planCode) {
        handleFieldChange('plan', planCode);
      }
    }
  }, [companyPlans]);

  // If user has sponsorId but somehow has Solo or Team selected, switch to Partner
  useEffect(() => {
    if (sponsorId && (plan === 'hb-agent-solo' || plan === 'hb-agent-team')) {
      handleFieldChange('plan', 'hb-agent-partner');
    }
  }, [sponsorId, plan]);

  // Track page load and form initialization
  useEffect(() => {
    trackPageLoad(getFormContext());
  }, []); // Only run once on mount

  // Track form progression based on plan changes
  useEffect(() => {
    if (plan) {
      trackFormProgression('current_section', 'enter', getFormState(), getFormContext());
    }
  }, [plan]);

  // Intersection Observer for testimonial animation
  useEffect(() => {
    const testimonialElement = document.querySelector('.well');

    if (!testimonialElement || showTestimonialAnimation) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Trigger when the element is intersecting and at least 25px is visible
          if (entry.isIntersecting) {
            setShowTestimonialAnimation(true);
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.05, // Trigger when 5% of the element is visible
        rootMargin: '0px 0px -25px 0px' // Start observing 25px before the element enters viewport
      }
    );

    observer.observe(testimonialElement);

    return () => observer.disconnect();
  }, [showTestimonialAnimation]);

  // Auto-apply promo code from URL parameter
  useEffect(() => {
    if (promoCode && promoCode.trim() && !isAutoApplyingPromo && appliedPromoCodes.length === 0) {
      console.log('Auto-applying promo code from URL:', promoCode);

      // Show the promo code section and set loading state
      updateField(dispatch, 'showPromoCode', true);
      setIsAutoApplyingPromo(true);
      setCurrentPromoInput(promoCode);

      // Delay to ensure Recurly is fully loaded
      setTimeout(async () => {
        try {
          console.log('Validating promo code:', promoCode);
          const validationResult = await validatePromoCode(promoCode, plan, subscriptionLength);

          if (validationResult.isValid) {
            console.log('Promo code is valid, applying:', validationResult);
            const newPromoCode = {
              code: promoCode.trim(),
              discount: validationResult.discount,
              discountInfo: validationResult.discountInfo,
              description: validationResult.discountInfo?.type === 'trial'
                ? `Trial: ${promoCode.trim()}`
                : validationResult.discountInfo?.type === 'percent'
                ? `${validationResult.discount}% off: ${promoCode.trim()}`
                : `Promo: ${promoCode.trim()}`
            };

            setAppliedPromoCodes(prev => [...prev, newPromoCode]);
            setCurrentPromoInput('');
            setPromoCodeValidation({ isValidating: false, isValid: true, error: null });

            // Clear the promoCode from form state
            updateField(dispatch, 'promoCode', '');
          } else {
            console.log('Promo code is invalid:', validationResult.error);
            setPromoCodeValidation({
              isValidating: false,
              isValid: false,
              error: validationResult.error || 'Invalid promo code'
            });
          }
        } catch (error) {
          console.error('Error auto-applying promo code:', error);
          setPromoCodeValidation({
            isValidating: false,
            isValid: false,
            error: 'Failed to validate promo code'
          });
        } finally {
          setIsAutoApplyingPromo(false);
        }
      }, 2000); // 2 second delay to ensure Recurly is loaded
    }
  }, [promoCode, plan, subscriptionLength, isAutoApplyingPromo, appliedPromoCodes.length, dispatch]);

  const selectNewPlanUrl = `${process.env.REACT_APP_MARKETING_URL}/pricing`;

  const selectedPlanDetails = getPlanDetails(plan);
  const baseCost = subscriptionLength === 'monthly' || subscriptionLength === 'none' ? selectedPlanDetails?.monthlyCost || 0 : selectedPlanDetails?.annualCost || 0;

  // Calculate total discount (both fixed amounts and percentages)
  const calculateTotalDiscount = () => {
    let fixedAmountDiscount = 0;
    let percentageDiscount = 0;

    appliedPromoCodes.forEach(promo => {
      if (promo.discountInfo?.type === 'trial') {
        // Skip trial discounts for monetary calculation
        return;
      } else if (promo.discountInfo?.type === 'percent') {
        // Calculate percentage discount on base cost
        percentageDiscount += baseCost * promo.discountInfo.rate;
      } else if (promo.discountInfo?.type === 'amount') {
        // Add fixed amount discount
        fixedAmountDiscount += promo.discount || 0;
      }
    });

    return fixedAmountDiscount + percentageDiscount;
  };

  const monetaryDiscount = calculateTotalDiscount();

  // Check if there's a free trial applied
  const hasFreeTrial = appliedPromoCodes.some(promo => promo.discountInfo?.type === 'trial');

  const totalDueToday = Math.max(0, baseCost + (selectedPlanDetails?.setupFee || 0) - monetaryDiscount);

  // Helper function to format currency with commas
  const formatCurrency = (amount, showDecimals = true) => {
    if (showDecimals) {
      return amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } else {
      return amount.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
  };

  // Helper function to format discount display based on type
  const formatDiscountDisplay = (promo) => {
    if (promo.discountInfo?.type === 'trial') {
      return `${promo.discountInfo.amount} ${promo.discountInfo.unit} free`;
    } else if (promo.discountInfo?.type === 'percent') {
      return `${promo.discount}% off`;
    } else {
      // Fixed amount discount
      return `-${formatCurrency(promo.discount)}`;
    }
  };

  const validateEmail = (value) => {
    if (!value.trim()) {
      return 'Email is required';
    }

    const email = value.trim();

    if (!isEmail(email, {
      allow_display_name: false,
      require_display_name: false,
      allow_utf8_local_part: false,
      require_tld: true,
      allow_ip_domain: false,
      domain_specific_validation: true,
      blacklisted_chars: '',
      host_blacklist: []
    })) {
      return 'Please enter a valid email address';
    }

    // Additional business rules
    const domain = email.split('@')[1]?.toLowerCase();

    // Reject some common fake/test domains
    const invalidDomains = ['test.com', 'example.com', 'localhost', 'test.test', 'fake.com'];
    if (invalidDomains.includes(domain)) {
      return 'Please enter a valid business email address';
    }

    // Check for disposable email domains (common ones)
    const disposableDomains = [
      '10minutemail.com', 'guerrillamail.com', 'mailinator.com', 'tempmail.org',
      'yopmail.com', 'throwaway.email', 'getnada.com', 'sharklasers.com'
    ];
    if (disposableDomains.includes(domain)) {
      return 'Please use a permanent business email address';
    }

    return '';
  };

  const handleFieldChange = (field, value) => {
    updateField(dispatch, field, value);

    // Track field changes
    trackFieldInteraction(field, 'change', value, getFormContext());

    if (field === 'email') {
      if (emailTouched) {
        const emailValidationError = validateEmail(value);
        setEmailError(emailValidationError);
        if (emailValidationError) {
          trackError('validation_error', emailValidationError, 'email', getFormContext());
        }
      }
    }

    // Track plan selection changes
    if (field === 'plan') {
      trackPlanSelection(value, plan, getFormContext());
    }

    // Track payment method selection
    if (field === 'paymentMethod') {
      trackPaymentMethodSelection(value, paymentMethod, getFormContext());
    }

    if (errors[field]) {
      clearErrors(dispatch);
    }
  };

  // Update name_on_account when first/last names change
  useEffect(() => {
    const fullName = `${cardFirstName} ${cardLastName}`.trim();
    updateField(dispatch, 'nameOnAccount', fullName);
  }, [cardFirstName, cardLastName, dispatch]);

  useEffect(() => {
    const recurly = window.recurly;
    const publicKey = process.env.REACT_APP_RECURLY_PUBLIC_KEY || 'ewr1-uKloW7PK9B1AXHiGZZ3OVb';
    recurly.configure(publicKey);

    // Only create Elements instance if it doesn't exist
    if (!elementsRef.current) {
      elementsRef.current = recurly.Elements();
    }

    if (paymentMethod === 'card') {
      // Set up a mutation observer to catch when iframes are added and immediately blur them
      observerRef.current = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const iframes = node.querySelectorAll ? node.querySelectorAll('iframe') : [];
              iframes.forEach(iframe => {
                iframe.setAttribute('tabindex', '-1');
                iframe.blur();
              });

              // If the node itself is an iframe
              if (node.tagName === 'IFRAME') {
                node.setAttribute('tabindex', '-1');
                node.blur();
              }
            }
          });
        });
      });

      // Start observing the payment method container
      const paymentContainer = document.querySelector('.payment-method-selection');
      if (paymentContainer) {
        observerRef.current.observe(paymentContainer, { childList: true, subtree: true });
      }

      const style = {
        fontSize: '1.25rem',
        fontFamily: "'SF Mono', 'Roboto Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
        fontColor: '#ffffff',
        backgroundColor: '#2a2a2a',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '12.8px',
        placeholder: {
          color: '#aaa'
        },
        invalid: {
          color: '#ff6b6b',
          border: '1px solid #ff6b6b'
        }
      }

      // Only create and attach if not already attached
      if (!attachedElementsRef.current.cardNumber) {
        const cardNumber = elementsRef.current.CardNumberElement({
          style: Object.assign({}, style, {

          })
        });
        cardNumber.attach('#recurly-card-number');
        attachedElementsRef.current.cardNumber = true;
      }

      if (!attachedElementsRef.current.cardMonth) {
        const cardMonth = elementsRef.current.CardMonthElement({
          style: Object.assign({}, style, {
            placeholder: {
              content: 'MM'
            }
          })
        });
        cardMonth.attach('#recurly-card-month');
        attachedElementsRef.current.cardMonth = true;
      }

      if (!attachedElementsRef.current.cardYear) {
        const cardYear = elementsRef.current.CardYearElement({
          style: Object.assign({}, style, {
            placeholder: {
              content: 'YYYY'
            }
          })
        });
        cardYear.attach('#recurly-card-year');
        attachedElementsRef.current.cardYear = true;
      }

      if (!attachedElementsRef.current.cardCvv) {
        const cardCvv = elementsRef.current.CardCvvElement({
          style: Object.assign({}, style, {
            placeholder: {
              content: 'CVV'
            },
          })
        });
        cardCvv.attach('#recurly-card-cvv');
        attachedElementsRef.current.cardCvv = true;
      }

            // Disconnect the observer after a reasonable time
      setTimeout(() => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      }, 1000);
    } else if (paymentMethod === 'bank') {
      // Disconnect the observer if it exists
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      Object.keys(attachedElementsRef.current).forEach(key => {
        attachedElementsRef.current[key] = false;
      });
    }
  }, [paymentMethod]);

  const handleConfirmSubscription = (e) => {
    e.preventDefault();
    clearErrors(dispatch);

    // Track form submission attempt
    trackFormSubmissionAttempt(getFormState(), getFormContext());

    const formData = {
      plan, capacity, paymentMethod, cardNumber, expiration, securityCode,
      cardFirstName, cardLastName, billingAddress, billingState, billingZip, billingCity, billingCountry, promoCode,
      accountNumber, accountNumberConfirmation, routingNumber, accountType, nameOnAccount,
      firstName, lastName, email, nmlsId, stateLicense, termsAccepted, subscriptionLength, sponsorId, referral,
      companyId, selectedOffice
    };

    const validationErrors = validatePaymentForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      console.log('Validation errors:', validationErrors);
      setErrors(dispatch, validationErrors);

      // Track validation errors
      Object.entries(validationErrors).forEach(([field, error]) => {
        trackError('form_validation_error', error, field, getFormContext());
      });

      trackFormSubmissionFailure('validation_errors', validationErrors, getFormContext());

      return;
    }

    setSubmitting(dispatch, true);

    const recurly = window.recurly;

    // Check if we have Elements and they're attached
    if (!elementsRef.current) {
      setErrors(dispatch, { submit: 'Payment elements not initialized' });
      setSubmitting(dispatch, false);
      return;
    }

    // Add a timeout to prevent hanging
    const tokenTimeout = setTimeout(() => {
      setErrors(dispatch, { submit: 'Tokenization timed out. Please try again.' });
      setSubmitting(dispatch, false);
    }, 10000); // 10 second timeout

    if (paymentMethod === 'card') {
      recurly.token(elementsRef.current, formRef.current, (err, token) => {
        clearTimeout(tokenTimeout);
        if (err) {
          console.log('Card tokenization error:', err);
          setErrors(dispatch, { submit: err.message });
          setSubmitting(dispatch, false);
        } else {
          sendSignupRequest(formData, token).then(() => {
            setSubmitting(dispatch, false);
          });
        }
      });
    } else if (paymentMethod === 'bank') {
      recurly.bankAccount.token(formRef.current, (err, token) => {
        clearTimeout(tokenTimeout);
        if (err) {
          setErrors(dispatch, { submit: err.message });
          setSubmitting(dispatch, false);
        } else {
          sendSignupRequest(formData, token).then(() => {
            setSubmitting(dispatch, false);
          });
        }
      });
    }
  };

  const sendSignupRequest = async (formData, token) => {
    console.log('Sending signup request with form data:', formData);
    console.log('Token:', token);
    const lenderPlans = ['hb-lender-starter', 'hb-lender-pro', 'fairway-individual', 'fairway-individual-starter'];
    const realEstatePlans = ['freemium-starter', 'hb-agent-solo', 'hb-agent-partner', 'hb-agent-team'];
    const insurancePlans = ['hb-insurance-agent-pro'];
    const titlePlans = ['hb-title-agent-pro'];

    const fullPlanCode = getFullPlanCode(formData.plan, formData.subscriptionLength);

    // Determine identifier type and value based on plan

    let identifierToken, identifierType;
    if (lenderPlans.includes(formData.plan)) {
      identifierToken = formData.nmlsId;
      identifierType = 'nmls';
    } else if (realEstatePlans.includes(formData.plan)) {
      identifierToken = formData.stateLicense;
      identifierType = 'state_license';
    } else if (insurancePlans.includes(formData.plan)) {
      identifierToken = null;
      identifierType = 'insurance';
    } else if (titlePlans.includes(formData.plan)) {
      identifierToken = null;
      identifierType = 'title';
    } else {
      // Default fallback
      identifierToken = null;
      identifierType = 'unknown';
    }

    const signupData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      identifier_token: identifierToken,
      identifier_type: identifierType,
      plan_code: fullPlanCode,
      billing_token: token.id,
      sponsor_id: formData.sponsorId,
      coupon_codes: appliedPromoCodes.map(promo => promo.code).join(','),
      referral: formData.referral,
      ...(selectedOffice && { office_profile_id: selectedOffice })
    };

    try {
      const result = await fetch(`${process.env.REACT_APP_MIKASA_URL}/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(signupData)
      });

      if (!result.ok) {
        const error = await result.json();
        if (error.errors.base) {
          throw new Error(error.errors.base.join(', '));
        } else if (error.errors) {
          throw new Error(error.errors.join(', '));
        } else {
          throw new Error(error.status);
        }
      }

      const responseData = await result.json();

      // Track successful conversion
      trackConversion({
        transaction_id: responseData.transaction_id || 'unknown',
        signup_method: paymentMethod,
        user_type: identifierType
      }, getFormContext(), totalDueToday, monetaryDiscount, appliedPromoCodes, getFullPlanCode);

      // Redirect to success page with auth URL as parameter
      navigate(`/success?authUrl=${encodeURIComponent(responseData.auth_callback_url)}`);
    } catch (error) {
      console.error('Signup error:', error);
      setSubmitting(dispatch, false);
      setErrors(dispatch, { submit: `Failed to process signup. ${error}` });

      // Track signup failure
      trackSignupFailure(error, 'api_request', getFormContext());
    }
  };

  const checkForFreemiumAvailability = async () => {
    if (!sponsorId) {
      return;
    }

    const result = await fetch(`${process.env.REACT_APP_MIKASA_URL}/customer-profiles/${sponsorId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (result.ok) {
      const responseData = await result.json();
      if (responseData['data']['attributes']['freemium-agents'] === 'enabled') {
        setFreemiumAvailable(true);
      }
    } else {
      console.error('Failed to check freemium availability');
    }
  };

  const validatePromoCode = async (code, plan, subscriptionLength) => {
    if (!code || !code.trim()) {
      return {
        isValid: false,
        discount: 0,
        error: 'Please enter a promo code'
      };
    }

    try {
      const recurly = window.recurly;
      const fullPlanCode = getFullPlanCode(plan, subscriptionLength);

      // Create a pricing object with the current plan
      const pricing = recurly.Pricing.Checkout();

      const subscription = recurly.Pricing.Subscription();
      subscription.plan(fullPlanCode);

      pricing.subscription(subscription);

      // Apply the coupon to see if it's valid and get discount
      await pricing.coupon(code);

      const discountType = pricing.items.coupon.discount.type;
      let discount = 0;
      let discountInfo = null;

      if (discountType === 'free_trial') {
        const discountTrial = pricing.items.coupon.discount.trial;
        discount = discountTrial.amount;
        const discountUnit = discountTrial.unit;
        discountInfo = {
          type: 'trial',
          amount: discount,
          unit: discountUnit
        };
      } else if (discountType === 'percent') {
        // Handle percentage discount
        const rate = pricing.items.coupon.discount.rate;
        discount = parseFloat(rate) * 100; // Convert to percentage (e.g., 0.2 -> 20)
        discountInfo = {
          type: 'percent',
          rate: parseFloat(rate)
        };
      } else {
        // Handle fixed amount discount
        discount = pricing.items.coupon.discount.amount.USD;
        discountInfo = {
          type: 'amount',
          amount: discount
        };
      }

      if (parseFloat(discount) > 0) {
        return {
          isValid: true,
          discount: parseFloat(discount),
          discountInfo: discountInfo,
          error: null
        };
      } else {
        return {
          isValid: true,
          discount: 0,
          discountInfo: null,
          error: null
        };
      }
    } catch (error) {
      console.error('Validation error:', error);
      return {
        isValid: false,
        discount: 0,
        error: 'Unable to validate promo code'
      };
    }
  };

  const applyPromoCode = async () => {
    if (!currentPromoInput.trim()) {
      setPromoCodeValidation({
        isValidating: false,
        isValid: false,
        discount: null,
        error: 'Please enter a promo code'
      });
      return;
    }

    // Check if code is already applied
    if (appliedPromoCodes.some(promo => promo.code.toLowerCase() === currentPromoInput.toLowerCase())) {
      const error = 'This promo code has already been applied';
      setPromoCodeValidation({
        isValidating: false,
        isValid: false,
        discount: null,
        error: error
      });
      trackPromoCodeEvent('apply_error', currentPromoInput, { error: 'already_applied' }, getFormContext());
      return;
    }

    setPromoCodeValidation({
      isValidating: true,
      isValid: null,
      discount: null,
      error: null
    });

    // Track promo code application attempt
    trackPromoCodeEvent('apply_attempt', currentPromoInput, null, getFormContext());

    try {
      // Validate the promo code using Recurly
      const validationResult = await validatePromoCode(currentPromoInput, plan, subscriptionLength);

      if (validationResult.isValid && validationResult.discount > 0) {
        // Validation successful - add the promo code
        const newPromoCode = {
          code: currentPromoInput.trim(),
          discount: validationResult.discount,
          discountInfo: validationResult.discountInfo,
          description: validationResult.discountInfo?.type === 'trial'
            ? `Trial: ${currentPromoInput.trim()}`
            : validationResult.discountInfo?.type === 'percent'
            ? `${validationResult.discount}% off: ${currentPromoInput.trim()}`
            : `Promo: ${currentPromoInput.trim()}`
        };

        setAppliedPromoCodes(prev => [...prev, newPromoCode]);
        setCurrentPromoInput('');
        setPromoCodeValidation({
          isValidating: false,
          isValid: true,
          discount: validationResult.discount,
          discountInfo: validationResult.discountInfo,
          error: null
        });

        // Track successful promo code application
        trackPromoCodeEvent('apply_success', newPromoCode.code, validationResult, getFormContext());

        // Clear validation state after a brief success message
        setTimeout(() => {
          setPromoCodeValidation({
            isValidating: false,
            isValid: null,
            discount: null,
            error: null
          });
        }, 2000);
      } else if (validationResult.isValid && validationResult.discount === 0) {
        const error = 'Valid code but no discount available for this plan';
        setPromoCodeValidation({
          isValidating: false,
          isValid: true,
          discount: 0,
          error: error
        });
        trackPromoCodeEvent('apply_error', currentPromoInput, { error: 'no_discount_available' }, getFormContext());
      } else {
        const error = validationResult.error || 'Invalid promo code';
        setPromoCodeValidation({
          isValidating: false,
          isValid: false,
          discount: null,
          error: error
        });
        trackPromoCodeEvent('apply_error', currentPromoInput, { error: 'invalid_code' }, getFormContext());
      }
    } catch (error) {
      console.error('Apply promo code error:', error);
      const errorMessage = 'Unable to validate promo code';
      setPromoCodeValidation({
        isValidating: false,
        isValid: false,
        discount: null,
        error: errorMessage
      });
      trackPromoCodeEvent('apply_error', currentPromoInput, { error: 'validation_failed' }, getFormContext());
    }
  };

  const removePromoCode = (codeToRemove) => {
    setAppliedPromoCodes(prev => prev.filter(promo => promo.code !== codeToRemove));
    trackPromoCodeEvent('remove', codeToRemove, null, getFormContext());
  };

  const isFirstNameValid = firstName.trim().length > 0;
  const isLastNameValid = lastName.trim().length > 0;
  const isEmailValid = !validateEmail(email);
  const isNmlsIdValid = nmlsId.trim().length > 0;
  const isStateLicenseValid = stateLicense.trim().length > 0;

  // Helper to get form context for tracking
  const getFormContext = () => ({
    plan,
    paymentMethod,
    subscriptionLength,
    sponsorId,
    promoCode
  });

  // Helper to get complete form state for tracking
  const getFormState = () => ({
    firstName,
    lastName,
    email,
    paymentMethod,
    termsAccepted,
    plan,
    nmlsId,
    stateLicense,
    subscriptionLength,
    cardFirstName,
    cardLastName,
    billingAddress,
    billingCity,
    billingState,
    billingZip,
    accountType,
    routingNumber,
    accountNumber,
    accountNumberConfirmation
  });



  return (
    <Layout>
      <form className="mt-16" ref={formRef} onSubmit={handleConfirmSubscription} noValidate>
        <div className=" single-page-container">

          <div className="form-section-single">
            <div className="form-section-single-header ">
              <div className="flex space-between align-center gap-2">
                <div>
                  <h1 className="mt-0 mb-0 uppercase tracking-wide">Checkout</h1>
                  <dl className="mt-0 flex flex-row gap-1 align-baseline">
                    <dt  className="lining-num header-price">${formatCurrency(totalDueToday, false)}</dt>
                    <dd className="header-label">{hasFreeTrial ? 'due today (setup only)' : 'due today'}</dd>
                  </dl>
                </div>
                <MemoIconHomebotApp style={{ transform: 'scale(0.9)' }} />
              </div>
            </div>

            {(plan === 'freemium-starter' || plan === 'hb-agent-solo' || plan === 'hb-agent-partner' || plan === 'hb-agent-team') && (
              <>
                <fieldset className="form-section-single-fieldset">
                  <div className="form-section-single-legend-container mt-8">
                    <legend className="form-section-single-legend">Choose your plan</legend>
                  </div>
                  <div className="plan-selection vertical">
                    {freemiumAvailable && (
                      <div
                        className={`plan-option capacity-option freemium-starter ${plan === 'freemium-starter' ? 'selected' : ''}`}
                        onClick={() => handleFieldChange('plan', 'freemium-starter')}
                        tabIndex={0}
                        role="button"
                        style={{ position: 'relative' }}
                      >
                        <MemoIconREAFreemium />
                        <div>
                          <h3>Freemium</h3>
                          <p className="sub-text">{getPlanDetails('freemium-starter').capacityAmount} {getPlanDetails('freemium-starter').capacityLabel}</p>
                        </div>
                        {plan === 'freemium-starter' && (
                          <span style={{ position: 'absolute', right: 32, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
                            <MemoIconCheckmarkCircle color="#CE14BE" checkMarkColor="#F7F5FA" />
                          </span>
                        )}
                      </div>
                    )}
                    {!sponsorId && (
                      <div
                        className={`plan-option capacity-option hb-agent-solo ${plan === 'hb-agent-solo' ? 'selected' : ''}`}
                        onClick={() => handleFieldChange('plan', 'hb-agent-solo')}
                        tabIndex={0}
                        role="button"
                        style={{ position: 'relative' }}
                      >
                        <div className="flex gap-2">
                          <MemoIconREASolo/>
                          <div>
                            <h3>Solo</h3>
                            <p className="sub-text">{getPlanDetails('hb-agent-solo').capacityAmount} {getPlanDetails('hb-agent-solo').capacityLabel}</p>
                          </div>
                        </div>
                        {plan === 'hb-agent-solo' && (
                          <span style={{ position: 'absolute', right: 32, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
                            <MemoIconCheckmarkCircle color="#EEFF41" />
                          </span>
                        )}
                      </div>
                    )}
                    <div
                      className={`plan-option capacity-option hb-agent-partner ${plan === 'hb-agent-partner' ? 'selected' : ''}`}
                      onClick={() => handleFieldChange('plan', 'hb-agent-partner')}
                      tabIndex={0}
                      role="button"
                      style={{ position: 'relative' }}
                    >
                      <div className="flex gap-2">
                      <MemoIconREAPartner />
                        <div>
                        <h3>Partner</h3>
                          <p className="sub-text">{getPlanDetails('hb-agent-partner').capacityAmount} {getPlanDetails('hb-agent-partner').capacityLabel}</p>
                          </div>
                      </div>
                      {plan === 'hb-agent-partner' && (
                        <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', borderRadius: '50%', padding: '4px', zIndex: 2 }}>
                          <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
                        </span>
                      )}
                    </div>
                    {!sponsorId && (
                      <div
                        className={`plan-option capacity-option hb-rea-team ${plan === 'hb-agent-team' ? 'selected' : ''}`}
                        onClick={() => handleFieldChange('plan', 'hb-agent-team')}
                        tabIndex={0}
                        role="button"
                        style={{ position: 'relative' }}
                      >
                        <div className="flex gap-2">
                          <MemoIconREATeam />
                          <div>
                          <h3>Team</h3>
                            <p className="sub-text">{getPlanDetails('hb-agent-team').capacityAmount} {getPlanDetails('hb-agent-team').capacityLabel}</p>
                            </div>
                        </div>
                        {plan === 'hb-agent-team' && (
                          <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
                            <MemoIconCheckmarkCircle  color="#499792" checkMarkColor="#fff" />
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                </fieldset>
              </>
            )}

            {(plan === 'hb-lender-starter' || plan === 'hb-lender-pro') && (
              <>
                <fieldset className="form-section-single-fieldset">
                  <div className="form-section-single-legend-container mt-8">
                    <legend className="form-section-single-legend">Choose your capacity</legend>
                  </div>
                  <div className="plan-selection vertical">
                    <div
                      className={`plan-option capacity-option hb-lender-starter ${plan === 'hb-lender-starter' ? 'selected' : ''}`}
                      onClick={() => handleFieldChange('plan', 'hb-lender-starter')}
                      tabIndex={0}
                      role="button"
                    >
                      <MemoIconLOStarter />
                      <div>
                        <p style={{ color: "#EEFF41" }}>Starter</p>
                        <h3>{getPlanDetails('hb-lender-starter').capacityAmount} <span className="option-title">{getPlanDetails('hb-lender-starter').capacityLabel}</span></h3>
                        <p className="sub-text">{getPlanDetails('hb-lender-starter').secondaryCapacityAmount} {getPlanDetails('hb-lender-starter').secondaryCapacityLabel}</p>
                      </div>
                      {plan === 'hb-lender-starter' && (
                        <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
                          <MemoIconCheckmarkCircle color="#EEFF41" />
                        </span>
                      )}
                    </div>
                    <div
                      className={`plan-option capacity-option hb-lender-pro ${plan === 'hb-lender-pro' ? 'selected' : ''}`}
                      onClick={() => handleFieldChange('plan', 'hb-lender-pro')}
                      tabIndex={0}
                      role="button"
                    >
                      <MemoIconLOPro />
                      <div>
                        <p style={{ color: "#fe8cc2" }}>Pro</p>
                        <h3>{getPlanDetails('hb-lender-pro').capacityAmount} <span className="option-title">{getPlanDetails('hb-lender-pro').capacityLabel}</span></h3>
                        <p className="sub-text">{getPlanDetails('hb-lender-pro').secondaryCapacityAmount} {getPlanDetails('hb-lender-pro').secondaryCapacityLabel}</p>
                      </div>
                      {plan === 'hb-lender-pro' && (
                        <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
                          <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
                        </span>
                      )}
                    </div>
                  </div>
                </fieldset>
              </>
            )}

            {plan === 'hb-insurance-agent-pro' && (
              <>
                <fieldset className="form-section-single-fieldset">
                  <div className="form-section-single-legend-container mt-8">
                    <legend className="form-section-single-legend">Your plan</legend>
                  </div>
                  <div className="plan-selection vertical">
                    <div
                      className="plan-option capacity-option hb-insurance-agent-pro selected"
                      tabIndex={0}
                      role="button"
                      style={{ position: 'relative' }}
                    >
                      <div className="flex gap-2">
                        <MemoIconREAPartner />
                        <div>
                          <h3>Insurance Agent Pro</h3>
                          <p className="sub-text">{getPlanDetails('hb-insurance-agent-pro').capacityAmount} {getPlanDetails('hb-insurance-agent-pro').capacityLabel}</p>
                        </div>
                      </div>
                      <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', borderRadius: '50%', padding: '4px', zIndex: 2 }}>
                        <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
                      </span>
                    </div>
                  </div>
                </fieldset>
              </>
            )}

            {plan === 'hb-title-agent-pro' && (
              <>
                <fieldset className="form-section-single-fieldset">
                  <div className="form-section-single-legend-container mt-8">
                    <legend className="form-section-single-legend">Your plan</legend>
                  </div>
                  <div className="plan-selection vertical">
                    <div
                      className="plan-option capacity-option hb-title-agent-pro selected"
                      tabIndex={0}
                      role="button"
                      style={{ position: 'relative' }}
                    >
                      <div className="flex gap-2">
                        <MemoIconREAPartner />
                        <div>
                          <h3>Title Agent Pro</h3>
                          <p className="sub-text">{getPlanDetails('hb-title-agent-pro').capacityAmount} {getPlanDetails('hb-title-agent-pro').capacityLabel}</p>
                        </div>
                      </div>
                      <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', borderRadius: '50%', padding: '4px', zIndex: 2 }}>
                        <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
                      </span>
                    </div>
                  </div>
                </fieldset>
              </>
            )}

            {companyId && companyPlans.length > 0 && (
              <>
                <fieldset className="form-section-single-fieldset">
                  <div className="form-section-single-legend-container mt-8">
                    <legend className="form-section-single-legend">
                      {companyPlans.length === 1 ? 'Your plan' : 'Choose your plan'}
                    </legend>
                  </div>
                  <div className="plan-selection vertical">
                    {companyPlans.map((companyPlan) => {
                      const companyPlanDetails = getPlanDetails(companyPlan.attributes?.code || companyPlan.attributes?.['plan-code']);
                      const isSelected = plan === companyPlanDetails.code;
                      
                      return (
                        <div
                          key={companyPlan.id}
                          className={`plan-option capacity-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => companyPlans.length > 1 && handleFieldChange('plan', companyPlanDetails.code)}
                          tabIndex={0}
                          role="button"
                          style={{ position: 'relative', cursor: companyPlans.length > 1 ? 'pointer' : 'default' }}
                        >
                          <div className="flex gap-2">
                            <MemoIconLOPro />
                            <div>
                              <h3>{companyPlanDetails.planName}</h3>
                              <p className="sub-text">{companyPlanDetails.capacityAmount} {companyPlanDetails.capacityLabel}</p>
                            </div>
                          </div>
                          {isSelected && (
                            <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', borderRadius: '50%', padding: '4px', zIndex: 2 }}>
                              <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </fieldset>
              </>
            )}

            {!companyId && <p className="mt-4"><span style={{ opacity: 0.85 }}>Not a {getPlanDetails(plan)?.customerType?.toLowerCase() || 'customer'}? </span><a className="pick-different-plan-link hover-underline" href={selectNewPlanUrl}>Switch your account type</a></p>}
            </div>

            {(plan === 'hb-lender-starter' || plan === 'hb-lender-pro') && (
            <div className="form-section-single">
              <fieldset className="form-section-single-fieldset">
                <div className="form-section-single-legend-container">
                  <legend className="form-section-single-legend">Billing period</legend>
                </div>
                <div className="plan-selection ">
                    <div
                      className={`plan-option flex-col align-start billing-period-option ${subscriptionLength === 'monthly' ? 'selected' : ''}`}
                      onClick={() => handleFieldChange('subscriptionLength', 'monthly')}
                      tabIndex={0}
                      role="button"
                    >
                      <p className="uppercase">Billed monthly</p>
                    <h3>${formatCurrency(getPlanDetails(plan).monthlyCost, false)} <span>/ month</span></h3>
                    {subscriptionLength === 'monthly' && (
                        <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
                          <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
                        </span>
                      )}
                    </div>
                    <div
                      className={`plan-option flex-col align-start billing-period-option ${subscriptionLength === 'annual' ? 'selected' : ''}`}
                      onClick={() => handleFieldChange('subscriptionLength', 'annual')}
                      tabIndex={0}
                      role="button"
                    >
                      <p className="uppercase">Billed annual</p>
                    <h3>${formatCurrency(getPlanDetails(plan).annualCost, false)} <span>/ year</span></h3>
                    {subscriptionLength === 'annual' && (
                        <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
                          <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
                        </span>
                      )}
                    </div>
                  </div>
                  {errors.subscriptionLength && (
                    <div className="mt-2">
                      <span className="error">{errors.subscriptionLength}</span>
                    </div>
                  )}
              </fieldset>
</div>
            )}

            <div className="form-section-single">
<fieldset className="form-section-single-fieldset">
              <div className="form-section-single-legend-container">
                <legend className="form-section-single-legend">Your contact info</legend>
              </div>
              <div className="form-grid">
                <div className="form-group flex-row">
                  <div className="form-group w-100" >
                    <label htmlFor="first-name">First name</label>
                    <div className="form-group-input-container flex">
                      <input
                        type="text"
                        id="first-name"
                        value={firstName}
                        onChange={(e) => handleFieldChange('firstName', e.target.value)}
                        onFocus={() => trackFieldInteraction('firstName', 'focus', null, getFormContext())}
                        onBlur={() => {
                          setFirstNameTouched(true);
                          trackFieldInteraction('firstName', 'blur', firstName, getFormContext());
                        }}
                        autoComplete="given-name"
                        className="font-mono w-100"
                      />
                      {/* Inline confirmation checkmark */}
                      {firstNameTouched && isFirstNameValid && !errors.firstName && (
                        <span style={{ position: 'absolute', right: 10, top: '50%', color: '#4caf50', fontSize: '1.5rem', transform: 'translateY(-50%)' }}><MemoIconCheckmarkCircle /></span>
                        )}
                        </div>
                    <div>
                      {firstNameTouched && !isFirstNameValid && (
                        <span className="error">First name is required</span>
                      )}
                      {!firstNameTouched && errors.firstName && <span className="error">{errors.firstName}</span>}
                    </div>
                  </div>
                  <div className="form-group w-100 mt-0" style={{ position: 'relative' }}>
                    <label htmlFor="last-name">Last name</label>
                    <div className="form-group-input-container flex">
                      <input
                        type="text"
                        id="last-name"
                        value={lastName}
                        onChange={(e) => handleFieldChange('lastName', e.target.value)}
                        onFocus={() => trackFieldInteraction('lastName', 'focus', null, getFormContext())}
                        onBlur={() => {
                          setLastNameTouched(true);
                          trackFieldInteraction('lastName', 'blur', lastName, getFormContext());
                        }}
                        autoComplete="family-name"
                        className="font-mono w-100"
                      />
                      {/* Inline confirmation checkmark */}
                      {lastNameTouched && isLastNameValid && !errors.lastName && (
                        <span style={{ position: 'absolute', right: 10, top: '50%', color: '#4caf50', fontSize: '1.5rem', transform: 'translateY(-50%)' }}><MemoIconCheckmarkCircle /></span>
                        )}
                    </div>
                    <div>
                      {lastNameTouched && !isLastNameValid && (
                        <span className="error">Last name is required</span>
                      )}
                      {!lastNameTouched && errors.lastName && <span className="error">{errors.lastName}</span>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="email">Email</label>
                <div className="form-group-input-container flex">
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    onFocus={() => trackFieldInteraction('email', 'focus', null, getFormContext())}
                    onBlur={() => {
                      setEmailTouched(true);
                      setEmailError(validateEmail(email));
                      trackFieldInteraction('email', 'blur', email, getFormContext());
                    }}
                    autoComplete="email"
                    className="font-mono w-100"
                  />
                  {/* Inline confirmation checkmark */}
                  {emailTouched && isEmailValid && !errors.email && (
                    <span style={{ position: 'absolute', right: 10, top: '25%', color: '#4caf50', fontSize: '1.5rem' }}><MemoIconCheckmarkCircle /></span>
                    )}
                </div>
                <div>
                  {(emailTouched && emailError) && <span className="error">{emailError}</span>}
                  {errors.email && <span className="error">{errors.email}</span>}
                </div>
              </div>
              {(plan === 'hb-lender-starter' || plan === 'hb-lender-pro' || plan === 'fairway-individual' || plan === 'fairway-individual-starter') && (
                <>
                  <div className="form-group full-width extra-margin-top w-50" style={{ position: 'relative' }}>
                    <label htmlFor="nmls-id">NMLS ID</label>
                    <div className="form-group-input-container flex">
                    <input
                      type="text"
                      id="nmls-id"
                      value={nmlsId}
                      onChange={(e) => handleFieldChange('nmlsId', e.target.value)}
                      onBlur={() => setNmlsIdTouched(true)}
                      autoComplete="off"
                      className="font-mono w-100"
                      />
                    </div>
                    {/* Inline confirmation checkmark */}
                    {nmlsIdTouched && isNmlsIdValid && !errors.nmlsId && (
                      <span style={{ position: 'absolute', right: 10, top: '50%', color: '#4caf50', fontSize: '1.5rem' }}><MemoIconCheckmarkCircle /></span>
                    )}
                    {errors.nmlsId && <span className="error">{errors.nmlsId}</span>}
                  </div>
                </>
                )}

              {(plan === 'freemium-starter' || plan === 'hb-agent-solo' || plan === 'hb-agent-partner' || plan === 'hb-agent-team') && (
              <>
                <div className="form-group full-width extra-margin-top w-50" style={{ position: 'relative' }}>
                    <label htmlFor="state-license">State license ID</label>
                    <div className="form-group-input-container flex">
                  <input
                    type="text"
                    id="state-license"
                    value={stateLicense}
                    onChange={(e) => handleFieldChange('stateLicense', e.target.value)}
                    onBlur={() => setStateLicenseTouched(true)}
                    autoComplete="off"
                    className="font-mono w-100"
                    />
                    </div>
                  {/* Inline confirmation checkmark */}
                  {stateLicenseTouched && isStateLicenseValid && !errors.stateLicense && (
                    <span style={{ position: 'absolute', right: 10, top: '50%', color: '#4caf50', fontSize: '1.5rem' }}><MemoIconCheckmarkCircle /></span>
                  )}
                  {errors.stateLicense && <span className="error">{errors.stateLicense}</span>}
                </div>
              </>
                )}

              {/* Office Selection Dropdown */}
              {companyId && (
                <div className="form-group full-width extra-margin-top">
                  <label htmlFor="office-select">Select Office</label>
                  {isLoadingCompanyData && (
                    <div style={{ padding: '1rem', color: '#aaa' }}>
                      Loading office information...
                    </div>
                  )}
                  {companyDataError && (
                    <div style={{ padding: '1rem', color: '#ff6b6b' }}>
                      Error loading offices: {companyDataError}
                    </div>
                  )}
                  {!isLoadingCompanyData && !companyDataError && officeProfiles.length > 0 && (
                    <>
                      <Select
                        id="office-select"
                        inputId="office-select"
                        placeholder="Select an office…"
                        options={officeProfiles.map(office => ({
                          value: office.id,
                          label: office.attributes?.name || office.attributes?.['office-name'] || `Office ${office.id}`
                        }))}
                        value={officeProfiles
                          .map(office => ({
                            value: office.id,
                            label: office.attributes?.name || office.attributes?.['office-name'] || `Office ${office.id}`
                          }))
                          .find(option => option.value === selectedOffice) || null}
                        onChange={option => handleFieldChange('selectedOffice', option ? option.value : '')}
                        isSearchable
                        classNamePrefix="react-select"
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            backgroundColor: '#00000045',
                            border: '1px solid #ffffff3f',
                            color: '#fff',
                            borderRadius: 8,
                            fontFamily: "'SF Mono', 'Roboto Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
                            fontSize: '1.25rem',
                            fontWeight: 400,
                            minHeight: 68,
                            height: 68,
                            boxShadow: 'none',
                            paddingLeft: 8,
                            paddingRight: 0,
                            outline: state.isFocused ? 'none' : undefined,
                            borderColor: state.isFocused ? '#ffffff3f' : '#ffffff3f',
                            alignItems: 'center',
                            '&:hover': {
                              borderColor: 'initial',
                            },
                            '&:focus-within': {
                              outline: '3px solid #ffffffdc',
                              outlineOffset: '2px',
                            },
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: '0 0.8rem',
                            height: 68,
                            display: 'flex',
                            alignItems: 'center',
                          }),
                          input: (provided) => ({
                            ...provided,
                            color: '#fff',
                            fontSize: '1.25rem',
                            margin: 0,
                            padding: 0,
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: '#fff',
                            fontSize: '1.25rem',
                            margin: 0,
                            padding: 0,
                          }),
                          indicatorSeparator: () => ({
                            display: 'none',
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: '#fff',
                            paddingRight: 12,
                            paddingLeft: 0,
                          }),
                          menu: (provided) => ({
                            ...provided,
                            backgroundColor: '#23232a',
                            color: '#fff',
                            borderRadius: 8,
                            marginTop: 2,
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            fontFamily: "'SF Mono', 'Roboto Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
                            backgroundColor: state.isSelected
                              ? '#F51E84'
                              : state.isFocused
                              ? 'rgba(255, 255, 255, 0.08)'
                              : '#23232a',
                            color: '#fff',
                            fontSize: '1.1rem',
                            padding: '10px 20px',
                            '&:active': {
                              backgroundColor: 'rgba(255, 255, 255, 0.16)',
                            },
                          }),
                        }}
                      />
                      {errors.selectedOffice && <span className="error">{errors.selectedOffice}</span>}
                    </>
                  )}
                  {!isLoadingCompanyData && !companyDataError && officeProfiles.length === 0 && corporateProfile && (
                    <div style={{ padding: '1rem', color: '#aaa' }}>
                      No offices available for this company.
                    </div>
                  )}
                </div>
              )}
            </fieldset>
</div>

<div className="form-section-single">
            <fieldset className="form-section-single-fieldset">
                <div className="form-section-single-legend-container">
                  <legend className="form-section-single-legend">Your payment details</legend>
                </div>
                <fieldset className="payment-method-fieldset">
                  <legend className="sr-only">Payment method selection</legend>
                  <div id="payment-method-group" className="plan-selection payment-method-selection" role="radiogroup" aria-labelledby="payment-method-group">
                <div
                    className={`plan-option payment-method ${paymentMethod === 'bank' ? 'selected' : ''}`}
                    onClick={() => handleFieldChange('paymentMethod', 'bank')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleFieldChange('paymentMethod', 'bank');
                      }
                    }}
                    tabIndex={0}
                    role="radio"
                    aria-checked={paymentMethod === 'bank'}
                    aria-label="Bank account payment method"
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="bank"
                      checked={paymentMethod === 'bank'}
                      onChange={() => handleFieldChange('paymentMethod', 'bank')}
                      style={{ display: 'none' }}
                      aria-hidden="true"
                    />
                    <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={`payment-icon ${paymentMethod === 'bank' ? ' selected' : ''}`}>
                      <path d="M0 6.31546V10.5268H1.05258V16.8412H0V19.9989H16.8412L19.9989 20L21.0515 19.9989V16.8412H19.9989V10.5268H21.0515V6.31546L10.5258 0L0 6.31546ZM4.21031 16.8412V10.5268H6.31546V16.8412H4.21031ZM9.47319 16.8412V10.5268H11.5783V16.8412H9.47319ZM16.8412 16.8412H14.7361V10.5268H16.8412V16.8412ZM12.6309 6.31546C12.6308 6.59198 12.5763 6.86578 12.4704 7.12123C12.3645 7.37667 12.2094 7.60876 12.0138 7.80424C11.8182 7.99972 11.5861 8.15477 11.3306 8.26053C11.0751 8.36628 10.8012 8.42068 10.5247 8.42061C10.2482 8.42054 9.97439 8.36601 9.71894 8.26012C9.4635 8.15424 9.23141 7.99908 9.03592 7.8035C8.84044 7.60792 8.6854 7.37575 8.57964 7.12025C8.47389 6.86475 8.41949 6.59093 8.41956 6.3144C8.4197 5.75594 8.64168 5.22041 9.03667 4.82562C9.43166 4.43083 9.9673 4.20911 10.5258 4.20925C11.0842 4.20939 11.6198 4.43137 12.0145 4.82636C12.4093 5.22135 12.6311 5.757 12.6309 6.31546Z"

                    />
</svg>

                    <span>Bank account</span>
                  </div>
                  <div
                    className={`plan-option payment-method ${paymentMethod === 'card' ? 'selected' : ''}`}
                    onClick={() => handleFieldChange('paymentMethod', 'card')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleFieldChange('paymentMethod', 'card');
                      }
                    }}
                    tabIndex={0}
                    role="radio"
                    aria-checked={paymentMethod === 'card'}
                    aria-label="Credit card payment method"
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={paymentMethod === 'card'}
                      onChange={() => handleFieldChange('paymentMethod', 'card')}
                      style={{ display: 'none' }}
                      aria-hidden="true"
                    />
                    <svg width="27" height="21" viewBox="0 0 27 21" fill="none" xmlns="http://www.w3.org/2000/svg" className={`payment-icon ${paymentMethod === 'card' ? ' selected' : ''}`}>
<path d="M0.00101471 5.29174H26.1123V4.18576C26.1123 1.73687 24.8643 0.5 22.3789 0.5H3.73446C1.24956 0.5 0.000507415 1.73687 0.000507415 4.18627L0.00101471 5.29174ZM0.00101471 16.8259C0.00101471 19.2753 1.24905 20.5 3.73446 20.5H22.3789C24.8638 20.5 26.1128 19.2753 26.1128 16.8259V7.99125H0L0.00101471 16.8259ZM3.9724 13.9245V11.7247C3.9724 11.0591 4.4361 10.5832 5.13774 10.5832H8.05083C8.75247 10.5832 9.21617 11.0591 9.21617 11.7247V13.9245C9.21617 14.6023 8.75247 15.066 8.05083 15.066H5.13723C4.43559 15.066 3.9724 14.6023 3.9724 13.9245Z"

                    />
</svg>

                    <span>Credit card</span>
                  </div>
                </div>
                </fieldset>
            </fieldset>




          <div className="form-grid mt-4">
            <div className="form-group full-width">
              <div className="name-inputs">
                <div>
                    <label htmlFor="first-name-card" className="mb-2 block">
                      {paymentMethod === 'card' ? 'First name on card' : 'First name on account'}
                    </label>
                    <div className="form-group-input-container flex">
                <input
                  type="text"
                  id="first-name-card"
                  data-recurly="first_name"
                  value={cardFirstName}
                  onChange={(e) => handleFieldChange('cardFirstName', e.target.value)}
                  autoComplete="given-name"
                  className="font-mono w-100"
                  />
                </div>
                </div>
                <div>
                    <label htmlFor="last-name-card" className="mb-2 block">
                      {paymentMethod === 'card' ? 'Last name on card' : 'Last name on account'}
                    </label>
                    <div className="form-group-input-container flex">
                  <input
                    type="text"
                    id="last-name-card"
                    data-recurly="last_name"
                    value={cardLastName}
                    onChange={(e) => handleFieldChange('cardLastName', e.target.value)}
                    autoComplete="family-name"
                    className="font-mono w-100"
                    />
                    </div>
                    </div>
                  <input
                    type="hidden"
                    data-recurly="name_on_account"
                    value={nameOnAccount}
                  />
              </div>
              {(errors.cardFirstName || errors.cardLastName) && (
                <span className="error">{errors.cardFirstName || errors.cardLastName}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="billing-address">
                {paymentMethod === 'card' ? 'Billing street address' : 'Street address'}
              </label>
              <div className="form-group-input-container flex">
                <input
                  type="text"
                  id="billing-address"
                  data-recurly="address1"
                  value={billingAddress}
                  onChange={(e) => handleFieldChange('billingAddress', e.target.value)}
                  autoComplete="address-line1"
                  className="font-mono w-100"
                />
              </div>
              {errors.billingAddress && <span className="error">{errors.billingAddress}</span>}
            </div>
            {/* What about an optional address line 2? */}
            <div className="form-group">
                <label htmlFor="billing-city">
                  {paymentMethod === 'card' ? 'Billing city' : 'City'}
                </label>
                <div className="form-group-input-container flex">
                <input
                  type="text"
                  id="billing-city"
                  data-recurly="city"
                  value={billingCity}
                  onChange={(e) => handleFieldChange('billingCity', e.target.value)}
                  autoComplete="address-level2"
                  className="font-mono w-100"
                />
              </div>
              {errors.billingCity && <span className="error">{errors.billingCity}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="billing-state">
                {paymentMethod === 'card' ? 'Billing state' : 'State'}
              </label>
              <Select
                id="billing-state"
                inputId="billing-state"
                placeholder="Select a state…"
                options={stateOptions}
                value={stateOptions.find(option => option.value === billingState) || null}
                onChange={option => handleFieldChange('billingState', option ? option.value : '')}
                isSearchable
                classNamePrefix="react-select"
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    backgroundColor: '#00000045',
                    border: '1px solid #ffffff3f',
                    color: '#fff',
                    borderRadius: 8,
                    fontFamily: "'SF Mono', 'Roboto Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
                    fontSize: '1.25rem',
                    fontWeight: 400,
                    minHeight: 68,
                    height: 68,
                    boxShadow: 'none',
                    paddingLeft: 8,
                    paddingRight: 0,
                    outline: state.isFocused ? 'none' : undefined,
                    borderColor: state.isFocused ? '#ffffff3f' : '#ffffff3f',
                    alignItems: 'center',
                    '&:hover': {
                      borderColor: 'initial',
                    },
                    '&:focus-within': {
                      outline: '3px solid #ffffffdc',
                      outlineOffset: '2px',
                    },
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    padding: '0 0.8rem',
                    height: 68,
                    display: 'flex',
                    alignItems: 'center',
                  }),
                  input: (provided) => ({
                    ...provided,
                    color: '#fff',
                    fontSize: '1.25rem',
                    margin: 0,
                    padding: 0,
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: '#fff',
                    fontSize: '1.25rem',
                    margin: 0,
                    padding: 0,
                  }),
                  indicatorSeparator: () => ({
                    display: 'none',
                  }),
                  dropdownIndicator: (provided) => ({
                    ...provided,
                    color: '#fff',
                    paddingRight: 12,
                    paddingLeft: 0,
                  }),
                  menu: (provided) => ({
                    ...provided,
                    backgroundColor: '#23232a',
                    color: '#fff',
                    borderRadius: 8,
                    marginTop: 2,
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    fontFamily: "'SF Mono', 'Roboto Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
                    backgroundColor: state.isSelected
                      ? '#F51E84'
                      : state.isFocused
                      ? 'rgba(255, 255, 255, 0.08)'
                      : '#23232a',
                    color: '#fff',
                    fontSize: '1.1rem',
                    padding: '10px 20px',
                    '&:active': {
                      backgroundColor: 'rgba(255, 255, 255, 0.16)',
                    },
                  }),
                }}
              />
              {errors.billingState && <span className="error">{errors.billingState}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="billing-zip">
                {paymentMethod === 'card' ? 'Billing zip code' : 'Zip code'}
              </label>
              <div className="form-group-input-container flex">
              <input
                type="text"
                id="billing-zip"
                data-recurly="postal_code"
                value={billingZip}
                onChange={(e) => handleFieldChange('billingZip', e.target.value)}
                autoComplete="postal-code"
                className="font-mono w-100"
                />
                </div>
              {errors.billingZip && <span className="error">{errors.billingZip}</span>}
            </div>
            <input
              type="hidden"
              id="billing-state"
              data-recurly="state"
              value={billingState}
              autoComplete="state"
            />
            <input
              type="hidden"
              id="billing-country"
              data-recurly="country"
              value={billingCountry}
              autoComplete="country"
            />

            {paymentMethod === 'card' && (
              <>
                <div className="form-group full-width mt-4">
                  <label htmlFor="card-number">Card number 🔒</label>
                  <div id="recurly-card-number">
                    {/* Recurly.js will mount the card number element here */}
                  </div>
                </div>
                <div className="form-group flex-row mt-4">
                  <div className="exp-field">
                    <label htmlFor="recurly-card-month">Exp. Month (MM)</label>
                    <div id="recurly-card-month" className="recurly-element-inline">
                      {/* Recurly.js will mount the card month element here */}
                    </div>
                  </div>
                  <div className="exp-field">
                    <label htmlFor="recurly-card-year">Exp. Year (YYYY)</label>
                    <div id="recurly-card-year" className="recurly-element-inline">
                      {/* Recurly.js will mount the card year element here */}
                    </div>
                  </div>
                  <div className="exp-field">
                    <label htmlFor="recurly-card-cvv">Security Code</label>
                    <div id="recurly-card-cvv" className="recurly-element-inline">
                      {/* Recurly.js will mount the card CVV element here */}
                    </div>
                    <div className="exp-field-icon">
                      <svg width="30" height="20" viewBox="0 0 30 20" xmlns="http://www.w3.org/2000/svg" fill="#FFF" role="img" aria-labelledby="cvcDesc"><g opacity="0.74"><path fillRule="evenodd" clipRule="evenodd" d="M25.2061 0.00488281C27.3194 0.112115 29 1.85996 29 4V11.3291C28.5428 11.0304 28.0336 10.8304 27.5 10.7188V8H1.5V16C1.5 17.3807 2.61929 18.5 4 18.5H10.1104V20H4L3.79395 19.9951C1.7488 19.8913 0.108652 18.2512 0.00488281 16.2061L0 16V4C0 1.85996 1.68056 0.112115 3.79395 0.00488281L4 0H25L25.2061 0.00488281ZM4 1.5C2.61929 1.5 1.5 2.61929 1.5 4V5H27.5V4C27.5 2.61929 26.3807 1.5 25 1.5H4Z"></path><path d="M27.5 12.7988C28.3058 13.1128 28.7725 13.7946 28.7725 14.6406C28.7722 15.4002 28.2721 15.9399 27.6523 16.1699C28.1601 16.3319 28.6072 16.6732 28.8086 17.2207C28.3597 18.6222 27.1605 19.6862 25.6826 19.9404C24.8389 19.7707 24.1662 19.2842 23.834 18.5H25C25.0914 18.5 25.1816 18.4939 25.2705 18.4844C25.5434 18.7862 25.9284 18.9501 26.3623 18.9502C27.142 18.9501 27.6922 18.5297 27.6924 17.79C27.6923 17.4212 27.5473 17.1544 27.2998 16.9795C27.4281 16.6786 27.5 16.3478 27.5 16V15.0527C27.5397 14.9481 27.5625 14.8309 27.5625 14.7002C27.5625 14.5657 27.5399 14.4422 27.5 14.3311V12.7988Z"></path><path d="M15.2207 18.5V18.8301H16.8799V19.9004H12.1104V18.8301H13.9902V18.5H15.2207Z"></path><path d="M19.9307 18.5L19.5762 18.7803H22.8369V19.9004H17.8164V18.8604L18.2549 18.5H19.9307Z"></path></g><path d="M26.3822 20.01C24.9722 20.01 23.8522 19.25 23.6422 17.81L24.8722 17.58C24.9922 18.45 25.6022 18.95 26.3622 18.95C27.1422 18.95 27.6922 18.53 27.6922 17.79C27.6922 17.05 27.1122 16.72 26.2822 16.72H25.5722V15.67H26.3022C27.0622 15.67 27.5622 15.34 27.5622 14.7C27.5622 14.07 27.1022 13.68 26.3922 13.68C25.6422 13.68 25.1322 14.18 24.9822 14.92L23.8122 14.76C24.0022 13.55 24.9822 12.61 26.4322 12.61C27.8822 12.61 28.7722 13.47 28.7722 14.64C28.7722 15.4 28.2722 15.94 27.6522 16.17C28.3422 16.39 28.9222 16.94 28.9222 17.89C28.9222 19.04 27.9522 20.01 26.3822 20.01Z"></path><path d="M17.8161 18.86L19.6161 17.38C20.5961 16.58 21.4761 15.87 21.4761 14.97C21.4761 14.23 21.0161 13.7 20.2561 13.7C19.5061 13.7 19.0161 14.29 19.0161 15C19.0161 15.23 19.0561 15.46 19.1361 15.68H17.9461C17.8461 15.39 17.8161 15.2 17.8161 14.93C17.8161 13.58 18.9261 12.61 20.2861 12.61C21.7861 12.61 22.7461 13.54 22.7461 14.89C22.7461 16.16 21.7861 17.03 20.7761 17.83L19.5761 18.78H22.8361V19.9H17.8161V18.86Z"></path><path d="M14.25 12.67H15.22V18.83H16.88V19.9H12.11V18.83H13.99V14.92H12.15V13.99L12.88 13.93C13.78 13.86 14.18 13.58 14.25 12.67Z"></path></svg>
                    </div>
                  </div>
                </div>
              </>
            )}
            {paymentMethod === 'bank' && (
              <>
                <div className="form-group mt-4">
                  <label htmlFor="recurly-account-type ">Account type</label>
                  <Select
                    id="recurly-account-type"
                    inputId="recurly-account-type"
                    placeholder="Select account type…"
                    options={accountTypeOptions}
                    value={accountTypeOptions.find(option => option.value === accountType) || null}
                    onChange={option => handleFieldChange('accountType', option.value)}
                    isSearchable={false}
                    classNamePrefix="react-select"
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        backgroundColor: '#00000045',
                        border: '1px solid ##00000045',
                        color: '#fff',
                        borderRadius: 8,
                        fontSize: '1.25rem',
                        fontWeight: 400,
                        fontFamily: "'SF Mono', 'Roboto Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
                        minHeight: 68,
                        height: 68,
                        boxShadow: 'none',
                        paddingLeft: 8,
                        paddingRight: 0,
                        outline: state.isFocused ? 'none' : undefined,
                        borderColor: state.isFocused ? '#ffffff3f' : '#ffffff3f',
                        '&:hover': {
                          borderColor: 'initial',
                        },
                        '&:focus-within': {
                          outline: '3px solid #ffffffdc',
                          outlineOffset: '2px',
                        },
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        padding: '0 0.8rem',
                        height: 68,
                        display: 'flex',
                        alignItems: 'center',
                      }),
                      input: (provided) => ({
                        ...provided,
                        color: '#fff',
                        fontSize: '1.25rem',
                        margin: 0,
                        padding: 0,
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#fff',
                        fontSize: '1.25rem',
                        margin: 0,
                        padding: 0,
                      }),
                      indicatorSeparator: () => ({
                        display: 'none',
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: '#fff',
                        paddingRight: 12,
                        paddingLeft: 0,
                      }),
                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: '#23232a',
                        color: '#fff',
                        borderRadius: 8,
                        marginTop: 2,
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        fontFamily: "'SF Mono', 'Roboto Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
                        backgroundColor: state.isSelected
                          ? '#F51E84'
                          : state.isFocused
                          ? 'rgba(255, 255, 255, 0.08)'
                          : '#23232a',
                        color: '#fff',
                        fontSize: '1.1rem',
                        padding: '10px 20px',
                        '&:active': {
                          backgroundColor: 'rgba(255, 255, 255, 0.16)',
                        },
                      }),
                    }}
                  />
                  <input
                    type="hidden"
                    data-recurly="account_type"
                    value={accountType}
                  />
                  {errors.accountType && <span className="error">{errors.accountType}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="recurly-routing-number">Routing number</label>
                  <div className="form-group-input-container flex">
                  <input
                    id="recurly-routing-number"
                    data-recurly="routing_number"
                    value={routingNumber}
                    onChange={(e) => handleFieldChange('routingNumber', e.target.value)}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={9}
                    autoComplete="off"
                    className="font-mono w-100"
                    />
                    </div>
                  {errors.routingNumber && <span className="error">{errors.routingNumber}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="recurly-account-number">Bank account number</label>
                  <div className="form-group-input-container flex">
                  <input
                    id="recurly-account-number"
                    data-recurly="account_number"
                    value={accountNumber}
                    onChange={(e) => handleFieldChange('accountNumber', e.target.value)}
                    type="text"
                    autoComplete="off"
                    className="font-mono w-100"
                    />
                    </div>
                  {errors.accountNumber && <span className="error">{errors.accountNumber}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="account-number-confirmation">Confirm account number</label>
                  <div className="form-group-input-container flex">
                  <input
                    data-recurly="account_number_confirmation"
                    value={accountNumberConfirmation}
                    onChange={(e) => handleFieldChange('accountNumberConfirmation', e.target.value)}
                    autoComplete="off"
                    type="text"
                    className="font-mono w-100"
                    />
                    </div>
                  {errors.accountNumberConfirmation && <span className="error">{errors.accountNumberConfirmation}</span>}
                </div>
                <input
                  type="hidden"
                  data-recurly="name_on_account"
                  value={nameOnAccount}
                />
              </>
            )}
          </div>

          <input type="hidden" data-recurly="token" />

          {!showPromoCode && (
            <button
              type="button"
              className="promo-code-link"
              onClick={() => {
                updateField(dispatch, 'showPromoCode', true);
                trackPromoCodeSectionOpened(getFormContext());
              }}
            >
              + Add promo code
            </button>
          )}

          {showPromoCode && (
            <div className="form-group full-width promo-code-group text-left">
              <label htmlFor="promo-code">Promo code</label>

              {/* Applied promo codes */}
              {appliedPromoCodes.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  {appliedPromoCodes.map((promo, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#1a1a1a',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      marginBottom: '0.5rem',
                      border: '1px solid #4caf50'
                    }}>
                      <div>
                        <span style={{ color: '#4caf50', fontWeight: 'bold' }}>{promo.code}</span>
                        <span style={{ color: '#aaa', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                          {formatDiscountDisplay(promo)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePromoCode(promo.code)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ff6b6b',
                          cursor: 'pointer',
                          fontSize: '1.2rem'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Auto-apply indicator */}
              {isAutoApplyingPromo && (
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#f0f8ff',
                  border: '1px solid #add8e6',
                  borderRadius: '4px',
                  color: '#2c5aa0'
                }}>
                  ⏳ Applying promo code from URL: {currentPromoInput}
                </div>
              )}

              {/* New promo code input */}
              <div style={{ position: 'relative', display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  id="promo-code"
                  value={currentPromoInput}
                  onChange={(e) => setCurrentPromoInput(e.target.value)}
                  onFocus={() => trackFieldInteraction('promoCode', 'focus', null, getFormContext())}
                  onBlur={() => trackFieldInteraction('promoCode', 'blur', currentPromoInput, getFormContext())}
                  autoComplete="off"
                  className="font-mono"
                  placeholder="Enter promo code"
                  style={{
                    maxWidth: '300px',
                    borderColor: '#444',
                    flex: 1
                  }}
                />
                <button
                  type="button"
                  onClick={applyPromoCode}
                  disabled={promoCodeValidation.isValidating || !currentPromoInput.trim()}
                  className="plan-option billing-period-option"
                  style={{
                    padding: '0.5rem 1rem',
                    cursor: (promoCodeValidation.isValidating || !currentPromoInput.trim()) ? 'not-allowed' : 'pointer',
                    color: '#fff',
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    flex: 'none',
                    justifyContent: 'center',
                    minWidth: '120px',
                    opacity: (promoCodeValidation.isValidating || !currentPromoInput.trim()) ? 0.6 : 1
                  }}
                >
                  {promoCodeValidation.isValidating ? 'Checking...' : 'Apply'}
                </button>
                </div>

               {/* Error messages */}
               {promoCodeValidation.error && (
                 <span className="error" style={{ fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                   {promoCodeValidation.error}
                 </span>
               )}

               {/* Success message */}
               {promoCodeValidation.isValid && promoCodeValidation.discount > 0 && (
                 <span style={{
                   color: '#4caf50',
                   fontSize: '0.8rem',
                   marginTop: '0.25rem',
                   display: 'block'
                 }}>
                   ✓ Promo code applied! {
                     promoCodeValidation.discountInfo?.type === 'trial'
                       ? `${promoCodeValidation.discountInfo.amount} ${promoCodeValidation.discountInfo.unit} free trial`
                       : promoCodeValidation.discountInfo?.type === 'percent'
                       ? `${promoCodeValidation.discount}% discount`
                       : `Discount: $${promoCodeValidation.discount.toFixed(2)}`
                   }
                 </span>
               )}
            </div>
          )}
          </div>

          <div className="form-section-single">
          <h2 className="form-section-single-legend">Confirmation</h2>
            <p className="op-90 leading-body text-md">
              You're about to take advantage of a subscription to Homebot's <strong>{selectedPlanDetails?.planName || 'Plan'} plan for {selectedPlanDetails?.customerType || 'Customer'}s</strong>.
            </p>
            <p className="op-90 leading-body text-md">After this, you can add clients right away 🎉</p>
            <div className="cost-summary-box">
              <div className="cost-item">
                <span className='op-80'>Subscription</span>
                <span className="weight-600 tabular-num font-mono">${formatCurrency(baseCost)}</span>
              </div>
              <div className="cost-item">
                <span className='op-80'>One-time setup fee</span>
                <span className="weight-600 tabular-num font-mono">${formatCurrency(selectedPlanDetails?.setupFee || 0)}</span>
              </div>
              {appliedPromoCodes.map((promo, index) => (
                <div key={index} className="cost-item">
                  <span className='op-80' style={{ color: '#4caf50' }}>{promo.description}</span>
                  <span className="weight-600 tabular-num font-mono" style={{ color: '#4caf50' }}>
                    {formatDiscountDisplay(promo)}
                  </span>
                </div>
              ))}
              <div className="cost-total">
                <span>Total Due Today</span>
                <span className='cost-total-price tabular-num'>${hasFreeTrial ? 0 : formatCurrency(totalDueToday)}</span>
              </div>
              {hasFreeTrial && (
                <div style={{
                  fontSize: '0.8rem',
                  color: '#aaa',
                  marginTop: '0.5rem',
                  textAlign: 'center'
                }}>
                  * Subscription billing will begin after your free trial period
                </div>
              )}
            </div>

            <div className="form-group checkbox-group flex flex-col mt-4">
              <label className="custom-checkbox checkbox-well ml-0">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onChange={(e) => {
                    handleFieldChange('termsAccepted', e.target.checked);
                    trackTermsAcceptance(e.target.checked, getFormContext());
                  }}
                />
                <span className="checkmark"></span>
                <span className="checkbox-label-text">
                  I accept Homebot's <a href="https://homebot.ai/terms-of-service" target="_blank" rel="noreferrer" className="hover-underline">Terms of Service</a> and <a href="https://homebot.ai/privacy-policy" target="_blank" rel="noreferrer" className="hover-underline">Privacy Policy</a>
                </span>
              </label>
              {errors.termsAccepted && <p className="error mt-2">{errors.termsAccepted}</p>}
            </div>
            <hr className="w-100" style={{ opacity: 0.08 }} />
            {errors.submit && <span className="error submit-error">{errors.submit}</span>}
            <p className="cancellation-policy text-center w-100 mx-auto">
              Cancel anytime in your billing settings <strong style={{ color: '#fff' }}>at least one day</strong> before the renewal date. Your plan automatically renews until cancelled.
            </p>
            <button
              type="submit"
              className="confirm-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Confirm & Subscribe'}
            </button>
            <div className="button-subtext mt-1">
              <p className="text-shadow-sm">
              🔒 Payments are secure and encrypted
              </p>
            </div>

          </div>
        </div>
      </form>

      <div className={`well text-center mw-600px w-100 mx-auto border-box mt-8 ${showTestimonialAnimation ? 'testimonial-animate' : ''}`}>
          <div className="photo-stack mx-auto">
          </div>
        <svg className="mt-2" width="60" height="8" viewBox="0 0 60 8" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clipPath="url(#clip0_1293_6501)">
<g clipPath="url(#clip1_1293_6501)">
<path d="M4 6.71429L1.57143 8L1 7.42857L1.71429 4.85714L0 3L0.285714 2.28571H2.42857L3.64286 0H4.35714L5.57143 2.28571H7.71429L8 3L6.28571 4.85714L7 7.42857L6.42857 8L4 6.71429Z" fill="white"/>
</g>
</g>
<g clipPath="url(#clip2_1293_6501)">
<g clipPath="url(#clip3_1293_6501)">
<path d="M17 6.71429L14.5714 8L14 7.42857L14.7143 4.85714L13 3L13.2857 2.28571H15.4286L16.6429 0H17.3571L18.5714 2.28571H20.7143L21 3L19.2857 4.85714L20 7.42857L19.4286 8L17 6.71429Z" fill="white"/>
</g>
</g>
<g clipPath="url(#clip4_1293_6501)">
<g clipPath="url(#clip5_1293_6501)">
<path d="M30 6.71429L27.5714 8L27 7.42857L27.7143 4.85714L26 3L26.2857 2.28571H28.4286L29.6429 0H30.3571L31.5714 2.28571H33.7143L34 3L32.2857 4.85714L33 7.42857L32.4286 8L30 6.71429Z" fill="white"/>
</g>
</g>
<g clipPath="url(#clip6_1293_6501)">
<g clipPath="url(#clip7_1293_6501)">
<path d="M43 6.71429L40.5714 8L40 7.42857L40.7143 4.85714L39 3L39.2857 2.28571H41.4286L42.6429 0H43.3571L44.5714 2.28571H46.7143L47 3L45.2857 4.85714L46 7.42857L45.4286 8L43 6.71429Z" fill="white"/>
</g>
</g>
<g clipPath="url(#clip8_1293_6501)">
<g clipPath="url(#clip9_1293_6501)">
<path d="M56 6.71429L53.5714 8L53 7.42857L53.7143 4.85714L52 3L52.2857 2.28571H54.4286L55.6429 0H56.3571L57.5714 2.28571H59.7143L60 3L58.2857 4.85714L59 7.42857L58.4286 8L56 6.71429Z" fill="white"/>
</g>
</g>
<defs>
<clipPath id="clip0_1293_6501">
<rect width="8" height="8" fill="white"/>
</clipPath>
<clipPath id="clip1_1293_6501">
<rect width="8" height="8" fill="white"/>
</clipPath>
<clipPath id="clip2_1293_6501">
<rect width="8" height="8" fill="white" transform="translate(13)"/>
</clipPath>
<clipPath id="clip3_1293_6501">
<rect width="8" height="8" fill="white" transform="translate(13)"/>
</clipPath>
<clipPath id="clip4_1293_6501">
<rect width="8" height="8" fill="white" transform="translate(26)"/>
</clipPath>
<clipPath id="clip5_1293_6501">
<rect width="8" height="8" fill="white" transform="translate(26)"/>
</clipPath>
<clipPath id="clip6_1293_6501">
<rect width="8" height="8" fill="white" transform="translate(39)"/>
</clipPath>
<clipPath id="clip7_1293_6501">
<rect width="8" height="8" fill="white" transform="translate(39)"/>
</clipPath>
<clipPath id="clip8_1293_6501">
<rect width="8" height="8" fill="white" transform="translate(52)"/>
</clipPath>
<clipPath id="clip9_1293_6501">
<rect width="8" height="8" fill="white" transform="translate(52)"/>
</clipPath>
</defs>

  </svg>

          <p className="weight-600 mb-1 mt-1">Loved by 15,000+ Loan Officers and <span className="nowrap">Real Estate Agents</span></p>

          </div>
    </Layout>
  );
};

export default SignUp;
